import { useTranslation } from "react-i18next";

import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";
import { User } from "services/users/user.types";
import { getCreateStatement } from "services/users/utils";

import useActiveEditorView from "pages/DevelopWorkspace/contexts/ActiveEditorViewContext/hooks/useActiveEditorView";

import { useMenu } from "components/ActionMenu/useMenu";
import { useAccessManager } from "components/App/accessManager";
import DefinitionCodePreview from "components/DefinitionCodePreview/DefinitionCodePreview";

import { RecordLabel } from "../RecordLabel";
import { Menu } from "../RecordMenu/RecordMenu";
import { RecordNode } from "../RecordNode";
import { useDeleteUser } from "./useDeleteUser";
import { useEditUser } from "./useEditUser";

import styles from "./styles.module.scss";

type Props = {
  user: User;
};

type UseUserMenuItemsParams = {
  user: User;
  menu: ReturnType<typeof useMenu>;
};

export const useUserMenuItems = ({ user, menu }: UseUserMenuItemsParams) => {
  const { userName } = user;
  const { t } = useTranslation();
  const { isAllowedTo } = useAccessManager();
  const { insertTextAtCursorPosition } = useActiveEditorView();
  const { openDeleteUser, deleteUserMarkup } = useDeleteUser();
  const { openEditUser, editUserMarkup } = useEditUser();

  const canEditUser = isAllowedTo(
    PrivilegeObjectType.user,
    userName,
    SqlRbacAction.MODIFY,
    {
      owner: user.userOwner,
    }
  );

  const deleteItems = canEditUser
    ? [
        [
          {
            key: "delete",
            text: t("workspace.user_menu.delete"),
            action: () => {
              openDeleteUser(userName);
            },
            testId: "delete-user-item",
          },
        ],
      ]
    : [];

  const editItems = canEditUser
    ? [
        {
          key: "edit",
          text: t("workspace.user_menu.edit"),
          action: () => {
            openEditUser(user);
          },
          testId: "edit-user-item",
        },
      ]
    : [];

  const items = [
    [
      ...editItems,
      {
        key: "view_definition",
        text: t("workspace.user_menu.view_definition"),
        renderContent: (props: { onContentClose: () => void }) => {
          const code = getCreateStatement(user, true);

          return (
            <DefinitionCodePreview
              code={code}
              onSecondaryButtonClick={() => {
                insertTextAtCursorPosition(`\n${code}`);
                menu.closeMenu();
                props.onContentClose();
              }}
            />
          );
        },
        testId: "view-user-definition-item",
      },
    ],
    ...deleteItems,
  ];

  return {
    items,
    modalsMarkup: (
      <>
        {deleteUserMarkup}
        {editUserMarkup}
      </>
    ),
  };
};

export const UserRecord = (props: Props) => {
  const { user } = props;
  const { userName } = user;
  const menu = useMenu();

  const { items, modalsMarkup } = useUserMenuItems({
    user,
    menu,
  });

  return (
    <>
      <RecordNode className={styles.node}>
        <div className={styles.node__inner}>
          <RecordLabel
            className={styles.label}
            testId={`user-label-${userName}`}
          >
            {userName}
          </RecordLabel>
          <Menu
            testId="user-more-icon"
            items={items}
            menu={menu}
          />
        </div>
      </RecordNode>
      {modalsMarkup}
    </>
  );
};
